<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import { getListPrompts } from '~~/src/api/lists'
import { useTopicsQuery } from '~~/src/composables/query/topic/useTopicsQuery'
import { type Elemental, ElementalTypeEnum, type ElementalVisibility } from '~~/src/types/elemental'
import type { List } from '~~/src/types/list'
import type { Prompt } from '~~/src/types/prompt'
import type { UserMinified } from '~~/src/types/user'

interface IProps {
  list: List
  variant?: 'main-item' | 'side-item' | 'search-item'
  lastItem?: boolean
}

const props = withDefaults(
  defineProps<IProps>(),
  {
    variant: 'main-item',
  },
)
const { list } = toRefs(props)

const { data: topics } = useTopicsQuery({
  query: {},
})
const user = useUser()
const { colors } = useTheme()
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smallerOrEqual('sm')

const showDetails = ref(false)
const prompts = ref<Prompt[]>([])

const pageSize = 3
const pageNumber = ref(1)
const canLoadMore = ref(true)
const isLoading = ref(true)
const isListOwner = ref(false)
const showExpansionToggler = ref(false)
const descriptionExpanded = ref(false)
const croppedContent = ref<string>('')

const listAsElemental = computed(() => {
  return ({ ...list.value, isList: true } as unknown) as Elemental
})

const elementalVisibility = computed<ElementalVisibility>(() => list.value.elementalsVisibility || 'NONE')

const elementalType = computed(() => useElementalTypes().value.find(t => t.name === 'list'))

const saveUpdate = useUpdateElementalSave()
watch(saveUpdate, (update) => {
  if (list.value && update?.id === list.value?.id) {
    const isAdded = update?.state === 'added'
    list.value.isSaved = isAdded
    if (isAdded) {
      list.value.nSaves++
      if (user.value)
        list.value.lastSaves.push(user.value as UserMinified)
    }
    else {
      list.value.nSaves = list.value.nSaves > 1 ? list.value.nSaves - 1 : 0
      if (list.value.lastSaves)
        list.value.lastSaves = list.value.lastSaves.filter(savedUser => savedUser.id !== user.value?.id)
    }
  }
})

onMounted(() => {
  const crop = cropDescriptionContent(stripHTMLDescription(list.value.description), 120)
  showExpansionToggler.value = crop.hasReachedLimit && isMobile.value
  croppedContent.value = crop.content
})

function toggleDetails() {
  showDetails.value = !showDetails.value
  if (showDetails.value && prompts.value.length === 0)
    loadPrompts()
}

async function loadPrompts(nextPage = false) {
  if (!list.value.nPrompts) {
    isLoading.value = false
    return
  }

  isLoading.value = true

  if (nextPage)
    pageNumber.value++

  const result = await getListPrompts(list.value.id, {
    page: pageNumber.value,
    size: pageSize,
    order_by: 'custom',
    order_dir: 'asc',
    is_preview: list.value.isPremium && elementalVisibility.value !== 'NONE',
  }) || []

  isLoading.value = false

  if (result !== null && (result.length > 0 || prompts.value.length > 0)) {
    isListOwner.value = true
    prompts.value = [
      ...prompts.value || [],
      ...result,
    ]
    canLoadMore.value = list.value.elementalsVisibility === 'ALL' && prompts.value.length < list.value.nPrompts
  }
  else {
    isListOwner.value = false
  }
}

const avatarSize = computed(() => {
  return isMobile.value ? 40 : 60
})

const optimizedListAvatar = computed(() => {
  if (!list.value.isPremium)
    return null

  if (!list.value.avatar)
    return '/prompt_avatar.png'

  return optimizeImage(list.value.avatar, {
    width: avatarSize.value * 2, height: avatarSize.value * 2,
  })
})

const typeName = computed(() => {
  if (elementalType.value?.id !== ElementalTypeEnum.List)
    return capitalizeFirstLetter(elementalType.value?.name)

  return `${capitalizeFirstLetter(list.value?.elementalType || 'combined')}${!list.value?.elementalType.match(/list/i) ? ' List' : ''}`
})
</script>

<template>
  <div
    flex flex-col gap-4 justify-center overflow-hidden cursor-pointer
    relative z-1 border-1 border-transparent border-b-gray-lighter hover:rounded-2 hover:border-gray-lighter
    transition-all
    class="group/list"
    :pt="variant === 'search-item' ? 3 : 5"
    :pb="(variant === 'search-item' || showDetails) ? 3 : 5"
    :px="variant === 'search-item' ? 2 : '10px'"
    :min-h="variant === 'search-item' ? 22 : 32"
    :class="[
      !showDetails && $style['card-default'],
      showDetails && '!border-soft-gray rounded-2',
      !showDetails && 'border-b-1 border-gray-lighter',
      !showDetails && lastItem && 'border-b-transparent hover:border-b-gray-lighter',
    ]"
    @click="() => toggleDetails()"
  >
    <div
      flex flex-col gap="6px"
      :min-h="variant === 'search-item' ? '' : '86px'"
      justify-center
    >
      <div flex gap-1>
        <div flex flex-grow-1 items-center gap="10px">
          <NuxtLink
            v-if="optimizedListAvatar && list.isPremium && variant !== 'search-item'"
            :to="`/list/${list.url}`" block flex-shrink-0
            @click.stop
          >
            <img
              loading="lazy"
              :src="optimizedListAvatar"
              :width="avatarSize" :height="avatarSize"
              :title="`${list.title} list avatar`"
              :alt="`${list.title} list avatar`"
              referrerpolicy="no-referrer"
              rounded-2 b-1 border-soft-gray bg-white object-cover object-center
            >
          </NuxtLink>

          <div flex flex-col gap="6px" flex-grow>
            <Text v-if="list.topicIds?.length" variant="caption" :color="colors.bluishDarker" :max-lines="1">
              <template v-for="(topicId, index) in list.topicIds.slice(0, 3)" :key="index">
                <span v-if="index !== 0"> / </span>
                <NuxtLink v-if="!isMobile" :to="`/topic/${topics.find(t => t.id === topicId)?.url}`" @click.stop>
                  {{ topics.find(t => t.id === topicId)?.name }}
                </NuxtLink>
                <span v-else>
                  {{ topics.find(t => t.id === topicId)?.name }}
                </span>
              </template>
            </Text>

            <NuxtLink :to="`/list/${list.url}`" flex items-center justify-between @click.stop>
              <Heading v-if="variant === 'main-item'" level="5-light" :max-lines="2">
                {{ list.title }}
              </Heading>
              <Heading v-else-if="variant === 'search-item'" level="5" :max-lines="2">
                {{ list.title }}
              </Heading>
              <Text v-else-if="variant === 'side-item'" variant="small" bold>
                {{ list.title }}
              </Text>

              <div v-if="variant === 'side-item'" i-mdi:chevron-right text-lg text-bluish-darker />
            </NuxtLink>

            <div
              flex="~ wrap" items-center
              :gap="(variant === 'search-item' || isMobile) ? 2 : 5"
              gap-y-1
              class="<sm:hidden"
            >
              <div
                flex items-center
                :gap="variant === 'search-item' ? 1 : 2"
              >
                <div v-if="list.isPrivate">
                  <div i-bx:hide text="12px" text-bluish-darker />
                </div>

                <Text v-if="variant === 'search-item'" variant="caption" :color="colors.bluishDarker">
                  By
                </Text>
                <Text v-else variant="caption" :color="colors.bluishSofter">
                  List
                </Text>

                <UserChip v-if="list.user" :user="list.user" />
              </div>

              <div flex gap-1 flex-shrink-0 items-center>
                <div v-if="list.lastSaves?.length" flex>
                  <template
                    v-for="(userSave, index) in list.lastSaves"
                    :key="userSave.id"
                  >
                    <img
                      v-if="index < 3"
                      loading="lazy"
                      :src="optimizeImage(userSave.avatar, {
                        width: 16 * 1.6,
                        height: 16 * 1.6,
                      })" referrerpolicy="no-referrer" w-4 h-4 b-1 border-white bg-white rounded-full object-cover object-center relative
                      :title="userSave.name"
                      :style="{ marginLeft: index > 0 ? '-6px' : '', zIndex: 3 - index }"
                    >
                  </template>
                </div>
                <Text variant="caption" color-bluish-darker flex-shrink-0>
                  {{ list.nSaves }} Saved
                </Text>
              </div>

              <Text variant="caption" color-bluish-darker flex-shrink-0>
                {{ formatPlural('Prompt', list?.nPrompts) }}
              </Text>

              <Text v-if="list.createdAt" variant="caption" color-bluish-darker flex-shrink-0>
                {{ formatTimeAgo(new Date(list.createdAt)) }}
              </Text>

              <TextV2 small class="flex items-center gap-0.5 text-darkGray border-softGray border-1 text-center rounded px-2 flex-shrink-0 !text-[10px] !leading-[20px] whitespace-nowrap">
                <span>
                  <div v-if="elementalType?.name === 'list'" i-mdi:folder-outline />
                  <div v-else-if="elementalType?.name === 'snippet'" i-mdi:slash-forward />
                  <div v-else-if="elementalType?.name === 'automation'" i-material-symbols-light:bolt-outline />
                  <div v-else-if="elementalType?.name === 'document'" i-mdi:file-outline />
                  <IconStar v-else-if="elementalType?.name === 'prompt'" stroke-color="currentColor" :size="10" />
                </span>
                <span>{{ typeName }}</span>
              </TextV2>
            </div>
          </div>
        </div>

        <div flex flex-col gap-1 items-end overflow-visible justify-center>
          <div flex-shrink-0>
            <template v-if="variant === 'search-item'">
              <ElementalSave
                v-if="elementalType" :element="listAsElemental" :elemental-type="elementalType"
              />
            </template>
            <template v-else>
              <ListUpvoteButton :list="list" variant="mini" />
            </template>
          </div>

          <div v-if="variant !== 'search-item'" flex items-center gap-1 flex-shrink-0>
            <div flex gap-1 md:opacity-0 transition-opacity class="group-hover/list:opacity-100">
              <PromptCopy :list="list" variant="mini" />
              <ElementalSave
                v-if="elementalType" :element="listAsElemental" variant="mini" :elemental-type="elementalType"
              />
            </div>
            <AppButton v-if="variant === 'main-item'" variant="text" type="button" class="!p-0 w-6 h-6">
              <div i-mdi:chevron-down text="18px" duration-125 transform :class="showDetails ? '-rotate-180' : 'rotate-0'" />
            </AppButton>
          </div>
        </div>
      </div>

      <div
        flex="~ wrap" items-center
        :gap="(variant === 'search-item' || isMobile) ? 4 : 5"
        gap-y-1
        class="sm:hidden"
      >
        <div
          flex items-center
          :gap="variant === 'search-item' ? 1 : 2"
        >
          <div v-if="list.isPrivate">
            <div i-bx:hide text="12px" text-bluish-darker />
          </div>

          <Text v-if="variant === 'search-item'" variant="caption" :color="colors.bluishDarker">
            By
          </Text>
          <Text v-else variant="caption" :color="colors.bluishSofter">
            List
          </Text>

          <UserChip v-if="list.user" :user="list.user" />
        </div>

        <div flex gap-1 flex-shrink-0 items-center>
          <div v-if="list.lastSaves?.length" flex>
            <template
              v-for="(userSave, index) in list.lastSaves"
              :key="userSave.id"
            >
              <img
                v-if="index < 3"
                loading="lazy"
                :src="optimizeImage(userSave.avatar, {
                  width: 16 * 1.6,
                  height: 16 * 1.6,
                })" referrerpolicy="no-referrer" w-4 h-4 b-1 border-white bg-white rounded-full object-cover object-center relative
                :title="userSave.name"
                :style="{ marginLeft: index > 0 ? '-6px' : '', zIndex: 3 - index }"
              >
            </template>
          </div>
          <Text variant="caption" color-bluish-darker flex-shrink-0>
            {{ list.nSaves }} Saved
          </Text>
        </div>

        <Text variant="caption" color-bluish-darker flex-shrink-0>
          {{ formatPlural('Prompt', list?.nPrompts) }}
        </Text>

        <Text v-if="list.createdAt" variant="caption" color-bluish-darker flex-shrink-0>
          {{ formatTimeAgo(new Date(list.createdAt)) }}
        </Text>

        <TextV2 small class="flex items-center gap-0.5 text-darkGray border-softGray border-1 text-center rounded px-2 flex-shrink-0 !text-[10px] !leading-[20px]">
          <span>
            <div v-if="elementalType?.name === 'list'" i-mdi:folder-outline />
            <div v-else-if="elementalType?.name === 'snippet'" i-mdi:slash-forward />
            <div v-else-if="elementalType?.name === 'automation'" i-material-symbols-light:bolt-outline />
            <div v-else-if="elementalType?.name === 'document'" i-mdi:file-outline />
            <IconStar v-else-if="elementalType?.name === 'prompt'" stroke-color="currentColor" :size="10" />
          </span>
          <span>{{ typeName }}</span>
        </TextV2>
      </div>
    </div>

    <TransitionFade>
      <div v-if="showDetails && variant === 'main-item'" flex flex-col justify-stretch>
        <div flex gap-4 mb-4>
          <div flex-grow-1 break-all>
            <Text variant="small" text-bluish-darker>
              <div v-if="(!isMobile && !showExpansionToggler) || descriptionExpanded" v-html="stripHTMLDescription(list?.description)" />
              <div v-else v-html="croppedContent" />
              <Text v-if="isMobile && showExpansionToggler" variant="small" :color="colors.bluishDarker" bold cursor-pointer class="!flex gap-1 mt-2" @click.prevent.stop="descriptionExpanded = !descriptionExpanded">
                {{ descriptionExpanded ? 'Read Less' : 'Read More' }}
                <div i-mdi:chevron-down duration-125 transform :class="descriptionExpanded ? '-rotate-180' : 'rotate-0'" />
              </Text>
            </Text>
          </div>
        </div>
        <template v-if="isLoading || list.nPrompts === 0 || (!list?.isPremium || isListOwner)">
          <PromptList
            variant="in-list"
            :elementals="prompts"
            :loading="isLoading"
            :can-load-more="canLoadMore"
            :show-upvote-button="false"
            :is-searching="true"
            @load-more="() => loadPrompts(true)"
          />
          <ProductContentListLocked v-if="!isLoading && list.elementalsVisibility === 'PARTIAL' && list.nPrompts > prompts.length" :size="2" />
        </template>
        <template v-else>
          <ProductContentListLocked :size="3" />
        </template>
      </div>
    </TransitionFade>
  </div>
</template>

<style module lang="scss">
.card-default::before {
  position: absolute;
  content: '';
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 8px;
  background-image: linear-gradient(226deg, #F6F6FF 0%, rgba(246, 246, 255, 0.00) 100%);
  z-index: -1;
  transition: opacity 0.35s linear;
  opacity: 0;
}

.card-default:hover {
  &::before {
    opacity: 1;
  }
}
</style>
